import React, {useEffect, useState} from "react";
import {useIsAuthenticated, useMsal} from "@azure/msal-react";
import {useNavigate} from "react-router-dom";
import {EventType, InteractionStatus} from "@azure/msal-browser";
import {loginRequest} from "../authConfig";
import {Client} from "@microsoft/microsoft-graph-client";

const getGraphClient = (accessToken) => {
    return Client.init({
        authProvider: (done) => {
            done(null, accessToken);
        },
    });
}

const getCurrentUserImageUrl = async (accessToken) => {
    const client = getGraphClient(accessToken);
    try {
        const photoBlob = await client.api("/me/photo/$value").get();
        return URL.createObjectURL(photoBlob);
    } catch (error) {
        console.error("Error when loading current user profile image:", error);
        return null;
    }
}

export const UserContext = React.createContext({
    loggedUser: undefined,
    isLogged: false,
    userImageUrl: undefined,
    login: () => {
    },
    logout: () => {
    },
    refetchLoggedUserDetails: () => {
        return Promise.resolve();
    },
});

const UserContextProvider = (props) => {
    const [loggedUser, setLoggedUser] = useState(undefined);
    const [userImageUrl, setUserImageUrl] = useState(undefined);
    const {instance, accounts, inProgress} = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const navigate = useNavigate();

    useEffect(() => {
        if (isAuthenticated && inProgress === InteractionStatus.None && loggedUser === undefined) {
            getUserDetailsFromApi();
        }
    }, [isAuthenticated, inProgress]);

    useEffect(() => {
        instance.enableAccountStorageEvents();

        const callbackId = instance.addEventCallback((message) => {
            if (message.eventType === EventType.LOGOUT_SUCCESS) {
                setLoggedUser(undefined);
                navigate("/login");
            }
        });
        return () => {
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
    }, []);

    const getUserDetailsFromApi = async () => {
        const user = await getAuthDetails();
        sessionStorage.setItem('token', user.token);
        setLoggedUser(user);
        const url = await getCurrentUserImageUrl(user.token);
        setUserImageUrl(url);
    };

    const loginHandler = () => {
        instance.loginRedirect(loginRequest).catch((e) => {
            console.error(e);
            setLoggedUser(undefined);
        });
    };

    const logoutHandler = () => {
        instance.logoutRedirect();
    };

    const getAuthDetails = async () => {
        try {
            const authenticationResult = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            });
            return Promise.resolve({token: authenticationResult.accessToken, name: authenticationResult.account.name});
        } catch (e) {
            logoutHandler();
            return Promise.reject("Unauthorized");
        }
    };

    const contextValue = {
        loggedUser: loggedUser,
        userImageUrl: userImageUrl,
        isLogged: isAuthenticated,
        login: loginHandler,
        logout: logoutHandler,
        refetchLoggedUserDetails: getUserDetailsFromApi
    };

    return <UserContext.Provider value={contextValue}>{props.children}</UserContext.Provider>;
};

export default UserContextProvider;
